import { DI } from '@microsoft/fast-foundation';
import { Auth, Connect, Message, SocketObservable } from '@genesislcap/foundation-comms';
import { ProfileVisibility, PROFILE_RIGHTS, CompanyRole } from '../utils/enums/generic.enum';
import { checkIfContainsAtLeastOneProfile, Profile } from '../utils/profiles';

export interface SIDE_VIEW_FILTER {
  SIDE_VIEW;
  RIGHT_CODE;
}

export interface IUserService {
  getAllUsers(): Promise<Message<any>>;
  getAllUserCompanies(): Promise<Message<any>>;
  getAllCompanyUsers(companyId: string): SocketObservable<Message<any>>;
  getUser(criteria?: string): SocketObservable<Message<any>>;
  getAllUserDetails(criteria?: string): SocketObservable<Message<any>>;
  getAllProfileUsers(): SocketObservable<Message<any>>;
  getAllValidProfileByCompanyRole(): Promise<Message<any>>;
  getAllUserRights(): SocketObservable<Message<any>>;
  getProfileByUser(username: String): Promise<Message<any>>;
  getAllProfiles(): SocketObservable<Message<any>>;
  getAllValidProfiles(): Promise<Message<any>>;
  setFilteredPermissionsProfiles(isAgent?: boolean);
  setCurrentUser();
  getCurrentUser();
  hasAdvisorRights(): boolean;
  getFilteredProfiles(): string[];
  getFilteredPermissions(): string[];
  hasBuyerAndSellerVisibility(): boolean;
  hasCascadeAdminRights(): boolean;
  hasManagementPageViewRight(): boolean;
  hasAdminEditUserRights(): boolean;
  getCurrentSide(): string;
  eventUpdateSide(side: string): Promise<any>;
}

class UserServiceImpl implements IUserService {
  constructor(
    @Connect private connect: Connect,
    @Auth private auth: Auth,
  ) {}
  private filteredPermissions: Array<string> = [];
  private filteredProfiles: Array<string> = [];
  private invalidPermissions: Array<SIDE_VIEW_FILTER> = [];
  private currentSide: string = ProfileVisibility.SELLER;
  private userObject: Object = null;

  getAllUsers(): Promise<Message<any>> {
    return this.connect.snapshot('USER');
  }

  getAllUserCompanies(): Promise<Message<any>> {
    return this.connect.snapshot('ALL_USER_COMPANY');
  }

  getAllCompanyUsers(companyId: string): SocketObservable<Message<any>> {
    const request: any = this.connect.stream(
      'ALL_USER_COMPANY',
      (streamMessage: Message) => {
        () => {};
      },
      (x) => {},
      { CRITERIA_MATCH: `CASCADE_COMPANY_ID == '${companyId}'` },
    );

    return request;
  }

  getUser(username: string): SocketObservable<Message<any>> {
    const request: any = this.connect.stream(
      'USER',
      (streamMessage: Message) => {
        () => {};
      },
      (x) => {},
      { CRITERIA_MATCH: `USER_NAME == '${username}'` },
    );

    return request;
  }

  async setCurrentUser() {
    await this.getUser(this.auth.currentUser?.username).subscribe({
      next: (streamMessage: Message) => {
       if (!this.userObject){
         this.userObject = streamMessage?.ROW?.[0] ?? null;
       }
      }
    });
  }

  getCurrentUser() {
    return this.userObject
  }

  getAllUserDetails(): SocketObservable<Message<any>> {
    const request: any = this.connect.stream(
      'ALL_USER_DETAILS',
      (streamMessage: Message) => {
        () => {};
      },
      (x) => {},
      (x) => {},
    );

    return request;
  }

  getAllProfileUsers(): SocketObservable<Message<any>> {
    const request: any = this.connect.stream(
      'ALL_PROFILE_USERS',
      (streamMessage: Message) => {
        () => {};
      },
      (x) => {},
      (x) => {},
    );

    return request;
  }

  getAllUserRights(): SocketObservable<Message<any>> {
    const request: any = this.connect.stream(
      'ALL_USER_RIGHTS',
      (streamMessage: Message) => {
        () => {};
      },
      (x) => {},
      (x) => {},
    );

    return request;
  }

  getProfileByUser(username: String): Promise<Message<any>> {
    const request: any = this.connect.snapshot('ALL_PROFILE_USERS', {
      CRITERIA_MATCH: `USER_NAME=='${username}'`,
    });

    return request;
  }

  getAllValidProfileByCompanyRole(): Promise<Message<any>> {
    return this.connect.request('COMPANY_TYPE_PERMISSION', {
      REQUEST: {},
    });
  }

  getAllProfiles(): SocketObservable<Message<any>> {
    const request: any = this.connect.stream(
      'ALL_PROFILES',
      (streamMessage: Message) => {
        () => {};
      },
      (x) => {},
      (x) => {},
    );

    return request;
  }

  getAllValidProfiles(): Promise<Message<any>> {
    const request: any = this.connect.request('VALID_PROFILES');

    return request;
  }

  getFilteredPermissions(): string[] {
    return this.filteredPermissions;
  }

  getFilteredProfiles(): string[] {
    return this.filteredProfiles;
  }

  async setFilteredPermissionsProfiles(isAgent?: boolean) {
    if (this.hasBuyerAndSellerVisibility() || isAgent) {
      if (isAgent) {
        this.currentSide = CompanyRole.ADVISOR;
      } else {
        await this.loadCurrentSide();
      }
      await this.loadInvalidPermissions();
      this.filteredPermissions = this.auth.currentUser?.permissions?.filter(
        (permission) =>
          !this.invalidPermissions.some(
            (invalidPermission) => permission === invalidPermission.RIGHT_CODE,
          ),
      );
      this.filteredProfiles = this.auth.currentUser?.profiles;
    } else {
      this.filteredProfiles = this.auth.currentUser?.profiles;
      this.filteredPermissions = this.auth.currentUser?.permissions;
    }
  }

  hasAdminEditUserRights(): boolean {
      return this.auth.currentUser.permissions.includes(
        PROFILE_RIGHTS.COMPANY_MANAGEMENT
      )
  }

  hasManagementPageViewRight(): boolean {
    return this.auth.currentUser.permissions.includes(
      PROFILE_RIGHTS.USER_MANAGEMENT_VIEW
    )
  }

  hasCascadeAdminRights(): boolean {
     return this.auth.currentUser.permissions.includes(
         PROFILE_RIGHTS.ADMIN_VISIBILITY
      )
  }

  hasAdvisorRights(): boolean {
      return this.auth.currentUser.permissions.includes(
        PROFILE_RIGHTS.ADVISOR
      )
  }

  hasBuyerAndSellerVisibility(): boolean {
    if (
      this.auth.currentUser.permissions.includes(PROFILE_RIGHTS.BUYER_VISIBILITY) &&
      this.auth.currentUser.permissions.includes(PROFILE_RIGHTS.SELLER_VISIBILITY)
    ) {
      return true;
    }

    return false;
  }

  getCurrentSide(): string {
    return this.currentSide;
  }

  async eventUpdateSide(sideView: string): Promise<any> {
    if (!sideView) return;

    const request: any = await this.connect.commitEvent('EVENT_SET_CURRENT_USER_SIDE_VIEW', {
      DETAILS: {
        SIDE_VIEW: sideView,
      },
      IGNORE_WARNINGS: true,
      VALIDATE: false,
    });
    return request;
  }

  private async loadCurrentSide() {
    const currentSideResponse = await this.connect.request('CURRENT_USER_SIDE_VIEW', {
      REQUEST: { USER_NAME: this.auth.currentUser.username },
    });
    if (currentSideResponse.REPLY[0]?.SIDE_VIEW) {
      this.currentSide = currentSideResponse.REPLY[0]?.SIDE_VIEW;
    }
  }

  private async loadInvalidPermissions() {
    const invalidPermissionsResponse = await this.connect.request('SIDE_VIEW_FILTER', {
      REQUEST: {},
    });

    if (invalidPermissionsResponse.REPLY) {
      this.invalidPermissions = invalidPermissionsResponse.REPLY.filter(
        (elm) => elm.SIDE_VIEW === this.currentSide,
      );
    }
  }
}

export const IUserService = DI.createInterface<IUserService>((x) => x.singleton(UserServiceImpl));
